<template>
  <div class="photoframe">
    <div class="photoframe__container">
      <img
        :src="photo.original"
        alt=""
        class="photoframe__image"
      />
      <img
        v-for="(frame, index) in frames"
        :key="`frame_${index}`"
        :src="frame.src"
        class="photoframe__frame"
        :class="`photoframe__frame_${frame.position}`"
        :style="{
          margin: cornerMargin,
          visibility: index === selectedFrameIndex ? 'visible' : 'hidden',
        }"
        alt=""
      />
    </div>
    <template v-if="frames.length > 1">
      <button
        type="button"
        class="photoframe__arrow photoframe__arrow_prev"
        @click="prevFrame"
      >
        <BaseIcon
          name="back"
          class="photoframe__icon"
        />
      </button>
      <button
        type="button"
        class="photoframe__arrow photoframe__arrow_next"
        @click="nextFrame"
      >
        <BaseIcon
          name="back"
          class="photoframe__icon"
        />
      </button>
    </template>
  </div>
</template>

<script>
import BaseIcon from '@/components/BaseIcon.vue';

import frameOne from '@/assets/images/frame_one.png';
import frameTwo from '@/assets/images/frame_two.png';

import CONSTANTS from '@/helpers/constants';

const {
  ACTION_SLIDE_PREV,
  ACTION_SLIDE_NEXT,
} = CONSTANTS;

const POSITION = Object.freeze({
  TOP: 'top',
  BOTTOM: 'bottom',
  TOP_LEFT: 'top_left',
  TOP_RIGHT: 'top_right',
  BOTTOM_LEFT: 'bottom_left',
  BOTTOM_RIGHT: 'bottom_right',
});

const GET_DRAW_NAME_BY_POSITION = Object.freeze({
  [POSITION.TOP]: 'getTopDrawParams',
  [POSITION.BOTTOM]: 'getBottomDrawParams',
  [POSITION.TOP_LEFT]: 'getTopLeftDrawParams',
  [POSITION.TOP_RIGHT]: 'getTopRightDrawParams',
  [POSITION.BOTTOM_LEFT]: 'getBottomLeftDrawParams',
  [POSITION.BOTTOM_RIGHT]: 'getBottomRightDrawParams',
});

const CORNER_MARGIN = 20;

export default {
  name: 'PhotoFrames',
  components: {
    BaseIcon,
  },
  props: {
    photo: {
      type: Object,
      required: true,
    },
  },
  data() {
    return {
      selectedFrameIndex: this.photo.frameIndex || 0,
    };
  },
  staticData: () => ({
    frames: [
      {
        src: frameOne,
        position: POSITION.BOTTOM,
      },
      {
        src: frameTwo,
        position: POSITION.BOTTOM_LEFT,
      },
    ],
    cornerMargin: `${CORNER_MARGIN}px`,
  }),
  computed: {
    selectedFrame() {
      return this.frames[this.selectedFrameIndex];
    },
  },
  created() {
    this.initActions();
  },
  methods: {
    prevFrame() {
      const lastIndex = this.frames.length - 1;
      const prevIndex = this.selectedFrameIndex - 1;
      this.selectedFrameIndex = (prevIndex < 0) ? lastIndex : prevIndex;
    },
    nextFrame() {
      const lastIndex = this.frames.length - 1;
      const nextIndex = this.selectedFrameIndex + 1;
      this.selectedFrameIndex = (nextIndex > lastIndex) ? 0 : nextIndex;
    },
    async selectFrame() {
      try {
        const [image, frame] = await Promise.all([
          this.createImage(this.photo.original),
          this.createImage(this.selectedFrame.src),
        ]);

        const canvas = document.createElement('canvas');
        canvas.width = image.naturalWidth;
        canvas.height = image.naturalHeight;

        const context = canvas.getContext('2d');
        const funcName = GET_DRAW_NAME_BY_POSITION[this.selectedFrame.position];
        const drawFunc = this[funcName];
        const drawParams = typeof drawFunc === 'function' ? drawFunc(image, frame) : [];

        context.drawImage(image, 0, 0, canvas.width, canvas.height);
        context.drawImage(frame, ...drawParams);

        this.photo.frameIndex = this.selectedFrameIndex;
        this.saveFile(canvas.toDataURL());
      } catch (e) {
        console.error('Select frame error:', e);
      }
    },
    removeFrame() {
      this.photo.frameIndex = null;
      return this.saveFile(this.photo.original);
    },
    saveFile(data) {
      this.photo.base64 = data;
      return this.$robot.filesystemService.saveFile(
        this.photo.mimeType,
        this.photo.base64,
        this.photo.fullPath,
      );
    },
    createImage(src) {
      return new Promise((resolve, reject) => {
        const image = new Image();
        image.onload = () => resolve(image);
        image.onerror = () => reject(src);
        image.src = src;
      });
    },
    calculateAspectRatioFit(srcWidth, srcHeight, maxWidth, maxHeight) {
      const ratio = Math.min(maxWidth / srcWidth, maxHeight / srcHeight);
      return [srcWidth * ratio, srcHeight * ratio];
    },
    getTopDrawParams(image, frame) {
      const [dWidth, dHeight] = this.calculateAspectRatioFit(
        frame.naturalWidth,
        frame.naturalHeight,
        image.naturalWidth,
        image.naturalHeight,
      );
      return [0, 0, dWidth, dHeight];
    },
    getBottomDrawParams(image, frame) {
      const [dWidth, dHeight] = this.calculateAspectRatioFit(
        frame.naturalWidth,
        frame.naturalHeight,
        image.naturalWidth,
        image.naturalHeight,
      );
      const dy = image.naturalHeight - dHeight;
      return [0, dy, dWidth, dHeight];
    },
    getTopLeftDrawParams() {
      return [CORNER_MARGIN, CORNER_MARGIN];
    },
    getTopRightDrawParams(image, frame) {
      const dx = image.naturalWidth - frame.naturalWidth - CORNER_MARGIN;
      return [dx, CORNER_MARGIN];
    },
    getBottomLeftDrawParams(image, frame) {
      const dy = image.naturalHeight - frame.naturalHeight - CORNER_MARGIN;
      return [CORNER_MARGIN, dy];
    },
    getBottomRightDrawParams(image, frame) {
      const dx = image.naturalWidth - frame.naturalWidth - CORNER_MARGIN;
      const dy = image.naturalHeight - frame.naturalHeight - CORNER_MARGIN;
      return [dx, dy];
    },
    initActions() {
      const rootActions = [
        [ACTION_SLIDE_PREV, this.prevFrame],
        [ACTION_SLIDE_NEXT, this.nextFrame],
      ];

      rootActions.forEach((action) => this.$root.$on(...action));

      this.$once('hook:beforeDestroy', () => {
        rootActions.forEach((action) => this.$root.$off(...action));
      });
    },
  },
};
</script>

<style lang="stylus">
.photoframe
  position relative
  display flex
  width 100%
  height 100%
  background-color inherit
  overflow hidden

  &__container
    position relative
    height 100%
    margin 0 auto

  &__image
    display block
    width auto
    height 100%

  &__frame
    display block
    position absolute
    z-index 3
    &_top
      abs top left
      width 100%
      margin 0 !important
    &_bottom
      abs bottom left
      width 100%
      margin 0 !important
    &_top_left
      abs top left
    &_top_right
      abs top right
    &_bottom_left
      abs bottom left
    &_bottom_right
      abs bottom right

  &__arrow
    abs top 50%
    width 3.6em
    height @width
    background-color $c_bright
    padding 1em
    color #fff
    border-radius 50%
    outline none
    border 0
    margin 0
    margin-top ((@height / 2) * -1)
    z-index 5
    &_prev
      left 1.25em
    &_next
      right 1.25em
      .icon
        transform rotate(180deg)
</style>
